import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import LogoHome from "../../../assets/images/logo-home.png";
import { AppContext } from "../../../context/app-context";
import { useSearchParams } from "react-router-dom";
import { PlanName } from "../../../constants";
import HeroStaticIos from "./HeroStaticIos";
import HeroStaticSilver from "./HeroStaticSilver";

const Header = ({ selectedPlanType }) => {
  const [state] = useContext(AppContext);
  const osType = state?.user?.osType;
  const [searchParams] = useSearchParams();
  const appVersion = searchParams.get("appVersion");

  const isSilverPlan =
    selectedPlanType === PlanName.ELITE_PLAN ||
    selectedPlanType === PlanName.YEARLY_ELITE_PLAN;

  // Must match CSS transition duration and diet plan page `plan-below` delay (2 × ANIM_MS).
  const ANIM_MS = 120;
  const exitTimerRef = useRef(null);
  const enterTimerRef = useRef(null);
  const didInitialHeroSyncRef = useRef(false);
  const renderSilverRef = useRef(false);

  const [renderSilver, setRenderSilver] = useState(false);
  const [animPhase, setAnimPhase] = useState("idle"); // "idle" | "enter" | "enter-active" | "exit" | "exit-active"

  renderSilverRef.current = renderSilver;

  // When parent first sets `selectedPlanType` (from "" → real plan), sync hero once — no animation.
  // Avoids wrong hero on Android default + avoids a fake "first transition" on load.
  useLayoutEffect(() => {
    if (!selectedPlanType || didInitialHeroSyncRef.current) return;
    didInitialHeroSyncRef.current = true;
    setRenderSilver(isSilverPlan);
    renderSilverRef.current = isSilverPlan;
    setAnimPhase("enter-active");
  }, [selectedPlanType, isSilverPlan]);

  // Animate only when the *plan* changes (Silver ↔ AI). Do not depend on `renderSilver` — updating it
  // mid-animation would re-run this effect and clear timers (broken first switch / messy transition).
  useEffect(() => {
    if (!selectedPlanType || !didInitialHeroSyncRef.current) return;
    if (renderSilverRef.current === isSilverPlan) return;

    const clearTimers = () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      if (enterTimerRef.current) {
        clearTimeout(enterTimerRef.current);
        enterTimerRef.current = null;
      }
    };

    clearTimers();

    setAnimPhase("exit");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAnimPhase("exit-active");
      });
    });

    exitTimerRef.current = setTimeout(() => {
      setRenderSilver(isSilverPlan);
      renderSilverRef.current = isSilverPlan;
      setAnimPhase("enter");

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimPhase("enter-active");
        });
      });

      enterTimerRef.current = setTimeout(() => {
        setAnimPhase("idle");
      }, ANIM_MS);
    }, ANIM_MS);

    return () => {
      clearTimers();
    };
  }, [isSilverPlan, selectedPlanType]);

  const planAnimClass = useMemo(() => {
    if (animPhase === "idle") return "";
    if (animPhase === "enter") return "plan-enter";
    if (animPhase === "enter-active") return "plan-enter-active";
    if (animPhase === "exit") return "plan-exit";
    if (animPhase === "exit-active") return "plan-exit-active";
    return "";
  }, [animPhase]);

  const heroClassName = [
    "diet-hero-static",
    !isSilverPlan ? "mb-3" : "",
    osType === "iOS" ? "diet-hero-static-ios" : "",
    osType === "android" ? "diet-hero-static-android" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className="pt-1 py-xl-8 dietplan">
      <div className="diet-hero-header mb-3">
          <div className="diet-hero-header-inner">
            <img
              className="img-fluid home-logo"
              loading="lazy"
              src={LogoHome}
              alt="Dr Trust 360 logo"
            />
            <button
              type="button"
              className="btn btn-primary-home bsb-btn-3xl rounded-pill ms-2"
            >
              PRO
            </button>
          </div>
        </div>

      <div className={heroClassName}>
        <div className={`plan-container ${planAnimClass}`}>
          {renderSilver ? <HeroStaticSilver /> : <HeroStaticIos />}
        </div>
      </div>
    </section>
  );
};

export default Header;
